import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import { WaitingAlert, DangerAlert, Table, InfoAlert, SecondaryLink, BtnBar } from '@tmapy/style-guide';

import { createDownloadLink, useSelector } from '../../hooks';
import { QUERY_STATE } from '../../constants';
import { useGeodesistsListResource } from '../../openapi/operations/geodesistsList';
import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { FilterBtn } from '../../app/components/FilterBtn';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';

import { GeodesistListItem } from './GeodesistListItem';
import { GeodesistFilter } from './GeodesistFilter';
import { geodesistMessages as msg, geodesistBtnMessages } from './geodesistMessages';

export const GeodesistList: React.FC = () => {
  const dispatch = useDispatch();
  const navigationType = useNavigationType();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const filters = useSelector((state) => state.filter.geodesistsList);
  const { manager } = useSelector((state) => state.app.config);

  const query: Record<string, string | number> = {};
  for (const [key, val] of Object.entries(filters)) {
    if (typeof val !== 'undefined' && val !== '') {
      (query as any)[key] = val;
    }
  }

  const { queryState, data, fetchMore } = useGeodesistsListResource(
    query,
    navigationType !== 'PUSH',
  );

  const geodesistList = data?.results ?? [];
  const geodesistCount = data?.count;

  function handleNextGeodesistsBtnClick() {
    if (data?.next) {
      fetchMore(data.next);
    }
  }

  function handleFilterBtnClick() {
    setFilterOpen(!isFilterOpen);
  }

  const isFiltered = Object.values(filters).some((filter) => filter);

  // CSV EXPORT
  const server = useSelector((state) => state.app.config.server);
  const queryFilterUrlString = useMemo(() => {
    return Object.entries(query)
      .filter(([_, value]) => value !== '')
      .map(([key, value]) => `&${key}=${value}`)
      .join('');
  }, [query]);

  const exportCsvLink = createDownloadLink(
    `${server}/dmx-server/geodesists/`,
    undefined,
    'download_csv',
    queryFilterUrlString,
  );

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(geodesistBtnMessages.filter)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='geodesistlist'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
            <PageTitle>
              <FormattedMessage {...msg.geodesistListPageTitle} />
            </PageTitle>
            <span className='sg-a-f-a' />

            <BtnBar>
              <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />

              {manager?.modules?.docExport && exportCsvLink && (
                <SecondaryLink size="sml" {...exportCsvLink} download>
                  <FormattedMessage {...geodesistBtnMessages.exportCsv} />
                </SecondaryLink>
              )}
            </BtnBar>
          </header>
          {queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...msg.geodesistListStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...msg.geodesistListStateFail} />
            </DangerAlert>
          )}
          {geodesistList.length > 0 ? (
            <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
              <div className='sg-a-mx--2'>
                <Table isClickable>
                  <thead>
                    <tr>
                      <th>
                        <FormattedMessage {...msg.officialNumber} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.name} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.isActive} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.hasCertification} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.note} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {geodesistList.map((geodesistItem) => (
                      <GeodesistListItem key={geodesistItem.id} {...geodesistItem} />
                    ))}
                  </tbody>
                </Table>
              </div>
              <LoadNextItemsBar
                count={geodesistList.length}
                total={geodesistCount!}
                queryState={queryState}
                onNextBtnClick={handleNextGeodesistsBtnClick}
              />
            </div>
          ) : (
            queryState === QUERY_STATE.SUCCESS && (
              <InfoAlert>
                <FormattedMessage {...msg.geodesistListStateFilteredEmpty} />
              </InfoAlert>
            )
          )}
        </article>
      </MainContent>
      <GeodesistFilter filters={filters} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
